import { useState, useCallback } from 'react'
import { useMutation } from '@tanstack/react-query'
import { uploadApi } from '../api/upload'
import { UploadFile } from '../types/image'

export const useUpload = () => {
  const [uploads, setUploads] = useState<UploadFile[]>([])

  const addFiles = useCallback((files: FileList | null) => {
    if (!files) return

    const newFiles: UploadFile[] = Array.from(files).map((file) => ({
      id: Math.random().toString(36).substr(2, 9),
      file,
      progress: 0,
      status: 'pending',
    }))

    setUploads((prev) => [...prev, ...newFiles])
    return newFiles.map((f) => f.id)
  }, [])

  const removeUpload = useCallback((id: string) => {
    setUploads((prev) => prev.filter((u) => u.id !== id))
  }, [])

  const clearCompleted = useCallback(() => {
    setUploads((prev) => prev.filter((u) => u.status !== 'completed'))
  }, [])

  const uploadMutation = useMutation({
    mutationFn: async ({
      file,
      metadata,
    }: {
      file: File
      metadata: { albumId: string; tags?: string[]; dateCaptured?: string }
    }) => {
      const session = await uploadApi.init({
        filename: file.name,
        contentType: file.type,
        fileSize: file.size,
      })

      await uploadApi.uploadToS3(session.presignedUrl, file, (progress) => {
        setUploads((prev) =>
          prev.map((u) =>
            u.file === file ? { ...u, progress, status: 'uploading' } : u
          )
        )
      })

      await uploadApi.complete(session.uploadId, metadata)

      return session.uploadId
    },
  })

  const startUpload = useCallback(
    async (id: string, metadata: { albumId: string; tags?: string[]; dateCaptured?: string }) => {
      const upload = uploads.find((u) => u.id === id)
      if (!upload) return

      setUploads((prev) =>
        prev.map((u) => (u.id === id ? { ...u, status: 'uploading' } : u))
      )

      try {
        await uploadMutation.mutateAsync({ file: upload.file, metadata })
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id ? { ...u, status: 'completed', progress: 100 } : u
          )
        )
      } catch (error) {
        setUploads((prev) =>
          prev.map((u) =>
            u.id === id
              ? { ...u, status: 'error', error: (error as Error).message }
              : u
          )
        )
      }
    },
    [uploads, uploadMutation]
  )

  return {
    uploads,
    addFiles,
    removeUpload,
    clearCompleted,
    startUpload,
    isUploading: uploadMutation.isPending,
  }
}
